import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';
import { GenerateInvoiceDto } from 'src/app/domain/models/dto/generate-invoice.dto';

import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { environment } from 'src/environments/environment';
import { AddExistingInvoiceDto } from 'src/app/domain/models/dto/add-existing-invoice.dto';
import { BehaviorSubject, map } from 'rxjs';
import { GenerateInvoiceOutboundDto } from 'src/app/domain/models/dto/generate-invoice-outbound.dto';
import { CreateDealDto, RateDto } from 'src/app/domain/models/dto/rate.dto';
import {
  CreateOrderDto,
  CreateOrderInboundDto,
} from 'src/app/domain/models/dto/create-order.dto';
import { CreateQuotationDto } from 'src/app/domain/models/dto/create-quotation.dto';
import {
  UpdateOrderWithInvoiceDto,
  UpdateOrderWithoutInvoiceDto,
} from 'src/app/domain/models/dto/update-order.dto';
import { SendAccountReceiverDto } from 'src/app/domain/models/dto/bank-account.dto';
import { FileHandle } from '../directives/file-drop.directive';
import { ReviewOrderDto } from 'src/app/domain/models/dto/review-order.dto';
import { CancelOrderDto } from 'src/app/domain/models/dto/cancel-order.dto';
import { TreasuryInfoDto } from 'src/app/domain/models/dto/treasury-info.dto';
import { RefusalPaymentDto } from 'src/app/domain/models/dto/refusalPayment.dto';
import { ConciliationDto } from 'src/app/domain/models/dto/conciliation.dto';
import { CreateOrderAdminDto } from 'src/app/domain/models/dto/create-order-admin.dto';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import { ApprovePaymentDto } from 'src/app/domain/models/dto/approvePayment.dto';
import { ExchangeOrder } from 'src/app/domain/models/exchange-order.model';
import { QuotationStatus } from 'src/app/domain/models/quotation-status.model';
import { ConfigsDto } from 'src/app/domain/models/dto/configs.dto';
import { QuotationProvidersOverviewResponse } from 'src/app/domain/models/dto/quotation-providers-overview.dto';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private listener: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(private http: HttpClient) {}

  get(status: string, rType: string) {
    return this.http.get<ResponseObjectModel<InboundOrderDto[]>>(
      `${environment.API_BASE_URL}/v1/orders?remmitanceType=${rType}`
    );
  }

  getOrderById(orderId: number, status?: string) {
    return this.http.get<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/${orderId}/staff?showStatus=${status}`
    );
  }

  getClientOrderById(orderId: number) {
    return this.http.get<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/${orderId}`
    );
  }

  checkQuotationAvailability() {
    return this.http.get<ResponseObjectModel<boolean>>(
      `${environment.API_BASE_URL}/v1/orders/quotation/availability-check`
    );
  }

  getExistentInboundPayment() {
    return this.http.get<ResponseObjectModel<boolean>>(
      `${environment.API_BASE_URL}/v1/orders/verify-exist-inbound-outside-payment`
    );
  }

  getAllAdmin(rType: string) {
    return this.http.get<ResponseObjectModel<InboundOrderDto[]>>(
      `${environment.API_BASE_URL}/v1/orders/staff?remmitanceType=${rType}`
    );
  }

  getAllAdminExchange(rType: string) {
    return this.http.get<ResponseObjectModel<InboundOrderDto[]>>(
      `${environment.API_BASE_URL}/v1/orders/exchange?remmitanceType=${rType}`
    );
  }

  getAdminByStatusFlow(statusFlow: string, rType: string) {
    return this.http.get<ResponseObjectModel<InboundOrderDto[]>>(
      `${environment.API_BASE_URL}/v1/orders/staff?${statusFlow}&remmitanceType=${rType}`
    );
  }

  sendToExchange(id: number) {
    return this.http.patch<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/${id}/exchange/boleto`,
      null
    );
  }

  effectivateOrder(id: number) {
    return this.http.put<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/admin/effectivate/${id}`,
      null
    );
  }

  getAdminByStatusFlowWithPagination(
    status: string,
    rType: string,
    pageNumber?: number,
    pageSize?: number,
    filters?: {
      statusFlow?: string;
      id?: string;
      currency?: string;
      name?: string;
      document?: string;
      createdDate?: string;
      slipCode?: string;
      orderSource?: string;
      personType?: string;
      agency?: string;
      sendOption?: string;
      confirmationTreasury?: boolean;
      value?: string;
      settlementFlow?: string;
      bankCurrencyTreasury?: string;
      levelingRateTreasury?: string;
      remmitanceType?: string;
      withSwift?: boolean;
    },
    search?: string
  ) {
    let filterParams = '';

    if (filters) {
      if (filters.statusFlow) {
        filterParams += `&flow=${encodeURIComponent(filters.statusFlow)}`;
      }
      if (filters.id) {
        filterParams += `&id=${encodeURIComponent(filters.id)}`;
      }
      if (filters.currency) {
        filterParams += `&currency=${encodeURIComponent(filters.currency)}`;
      }
      if (filters.name) {
        filterParams += `&name=${encodeURIComponent(filters.name)}`;
      }
      if (filters.document) {
        filterParams += `&document=${encodeURIComponent(filters.document)}`;
      }
      if (filters.createdDate) {
        filterParams += `&createdDate=${encodeURIComponent(
          filters.createdDate
        )}`;
      }
      if (filters.slipCode) {
        filterParams += `&slipCode=${encodeURIComponent(filters.slipCode)}`;
      }
      if (filters.confirmationTreasury !== null && filters.confirmationTreasury !== undefined) {
        filterParams += `&confirmationTreasury=${encodeURIComponent(filters.confirmationTreasury)}`;
      }
      if (filters.agency) {
        filterParams += `&agency=${encodeURIComponent(filters.agency)}`;
      }
      if (filters.sendOption) {
        filterParams += `&deliveryOption=${encodeURIComponent(
          filters.sendOption
        )}`;
      }
      if (filters.value) {
        filterParams += `&amount=${encodeURIComponent(filters.value)}`;
      }
      if (filters.settlementFlow) {
        filterParams += `&settlementFlow=${encodeURIComponent(
          filters.settlementFlow
        )}`;
      }
      if (filters.bankCurrencyTreasury) {
        filterParams += `&bankCurrencyTreasury=${encodeURIComponent(
          filters.bankCurrencyTreasury
        )}`;
      }
      if (filters.levelingRateTreasury) {
        filterParams += `&levelingRateTreasury=${encodeURIComponent(
          filters.levelingRateTreasury
        )}`;
      }
      if (filters.orderSource) {
        filterParams += `&orderSource=${encodeURIComponent(
          filters.orderSource
        )}`;
      }
      if (filters.personType) {
        filterParams += `&personType=${encodeURIComponent(filters.personType)}`;
      }
      if (filters.remmitanceType) {
        filterParams += `&remmitanceType=${encodeURIComponent(filters.remmitanceType)}`;
      }
      if (filters.withSwift) {
        filterParams += `&withSwift=${encodeURIComponent(filters.withSwift)}`;
      }
    }

    if (search) {
      filterParams += `&search=${encodeURIComponent(search)}`;
    }

    if (status) {
      filterParams += `&${status}`;
    }

    if (rType) {
      filterParams += `&remmitanceType=${rType}`;
    }

    return this.http.get<
      ResponseObjectModel<ResponseTableModel<InboundOrderDto[]>>
    >(
      `${environment.API_BASE_URL}/v1/orders/staff/additional-flow?pageNumber=${pageNumber}&pageSize=${pageSize}${filterParams}`
    );
  }

   getAdminByStatusFlowTabsWithPagination(
    status: string,
    rType: string,
    pageNumber?: number,
    pageSize?: number,
    filters?: {
      statusFlow?: string;
      id?: string;
      currency?: string;
      name?: string;
      document?: string;
      createdDate?: string;
      slipCode?: string;
      orderSource?: string;
      personType?: string;
      agency?: string;
      sendOption?: string;
      confirmationTreasury?: boolean;
      value?: string;
      settlementFlow?: string;
      bankCurrencyTreasury?: string;
      levelingRateTreasury?: string;
      remmitanceType?: string;
      withSwift?: boolean;
      spread?: string;
    },
    search?: string
  ) {
    let filterParams = '';

    if (filters) {
      if (filters.statusFlow) {
        filterParams += `&flow=${encodeURIComponent(filters.statusFlow)}`;
      }
      if (filters.id) {
        filterParams += `&id=${encodeURIComponent(filters.id)}`;
      }
      if (filters.currency) {
        filterParams += `&currency=${encodeURIComponent(filters.currency)}`;
      }
      if (filters.name) {
        filterParams += `&name=${encodeURIComponent(filters.name)}`;
      }
      if (filters.document) {
        filterParams += `&document=${encodeURIComponent(filters.document)}`;
      }
      if (filters.createdDate) {
        filterParams += `&createdDate=${encodeURIComponent(
          filters.createdDate
        )}`;
      }
      if (filters.slipCode) {
        filterParams += `&slipCode=${encodeURIComponent(filters.slipCode)}`;
      }
      if (filters.confirmationTreasury !== null && filters.confirmationTreasury !== undefined) {
        filterParams += `&confirmationTreasury=${encodeURIComponent(filters.confirmationTreasury)}`;
      }
      if (filters.agency) {
        filterParams += `&agency=${encodeURIComponent(filters.agency)}`;
      }
      if (filters.sendOption) {
        filterParams += `&deliveryOption=${encodeURIComponent(
          filters.sendOption
        )}`;
      }
      if (filters.value) {
        filterParams += `&amount=${encodeURIComponent(filters.value)}`;
      }
      if (filters.spread !== null && filters.spread !== undefined && filters.spread !== '') {
        filterParams += `&spread=${encodeURIComponent(filters.spread)}`;
      }
      if (filters.settlementFlow) {
        filterParams += `&settlementFlow=${encodeURIComponent(
          filters.settlementFlow
        )}`;
      }
      if (filters.bankCurrencyTreasury) {
        filterParams += `&bankCurrencyTreasury=${encodeURIComponent(
          filters.bankCurrencyTreasury
        )}`;
      }
      if (filters.levelingRateTreasury) {
        filterParams += `&levelingRateTreasury=${encodeURIComponent(
          filters.levelingRateTreasury
        )}`;
      }
      if (filters.orderSource) {
        filterParams += `&orderSource=${encodeURIComponent(
          filters.orderSource
        )}`;
      }
      if (filters.personType) {
        filterParams += `&personType=${encodeURIComponent(filters.personType)}`;
      }
      if (filters.remmitanceType) {
        filterParams += `&remmitanceType=${encodeURIComponent(filters.remmitanceType)}`;
      }
      if (filters.withSwift) {
        filterParams += `&withSwift=${encodeURIComponent(filters.withSwift)}`;
      }
    }

    if (search) {
      filterParams += `&search=${encodeURIComponent(search)}`;
    }

    if (status) {
      filterParams += `&${status}`;
    }

    if (rType) {
      filterParams += `&remmitanceType=${rType}`;
    }

    return this.http.get<
      ResponseObjectModel<ResponseTableModel<InboundOrderDto[]>>
    >(
      `${environment.API_BASE_URL}/v1/orders/staff?pageNumber=${pageNumber}&pageSize=${pageSize}${filterParams}`
    );
  }

  getClientWithPagination(
    rType: string,
    pageNumber?: number,
    pageSize?: number
  ) {
    return this.http.get<
      ResponseObjectModel<ResponseTableModel<InboundOrderDto[]>>
    >(
      `${environment.API_BASE_URL}/v1/orders?remmitanceType=${rType}&pageNumber=${pageNumber}&pageSize=${pageSize}`
    );
  }

  getAdminExchangeByStatusFlow(
    statusFlow: string,
    rType: string,
    pageNumber?: number,
    pageSize?: number,
    search?: string
  ) {
    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}&${statusFlow}&remmitanceType=${rType}`;

    if (search) {
      queryParams += `&filter=${encodeURIComponent(search)}`;
    }

    return this.http.get<
      ResponseObjectModel<ResponseTableModel<InboundOrderDto[]>>
    >(`${environment.API_BASE_URL}/v1/orders/exchange?${queryParams}`);
  }

  getExchangeOrders(pageNumber?: number, pageSize?: number, search?: string) {
    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}`;

    if (search) {
      queryParams += `&filter=${encodeURIComponent(search)}`;
    }

    return this.http.get<
      ResponseObjectModel<ResponseTableModel<ExchangeOrder[]>>
    >(`${environment.API_BASE_URL}/v1/orders/exchange?${queryParams}`);
  }

  get listenerUpdate() {
    return this.listener.asObservable();
  }

  updateOrderView() {
    this.listener.next(true);
  }

  public createQuotation(orderId: number, createQuotation: CreateQuotationDto) {
    return this.http.put<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/quotation-order/${orderId}`,
      createQuotation
    );
  }

  public createInboundOrder(order: GenerateInvoiceDto) {
    return this.http.post<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/inbound`,
      order
    );
  }

  public createNewInboundOrder(order: CreateOrderInboundDto) {
    return this.http.post<ResponseObjectModel<CreateOrderInboundDto>>(
      `${environment.API_BASE_URL}/v1/orders/create-order-inbound`,
      order
    );
  }

  public sendAccountReceiver(account: SendAccountReceiverDto) {
    return this.http.post<ResponseObjectModel<SendAccountReceiverDto>>(
      `${environment.API_BASE_URL}/v1/orders/bank-account-receiver`,
      account
    );
  }

  public createOutboundOrder(order: CreateOrderDto) {
    return this.http.post<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/outbound`,
      order
    );
  }

  public updateOrderWithoutInvoice(updateOrder: UpdateOrderWithoutInvoiceDto) {
    return this.http.post<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/update-finality-order-inbound`,
      updateOrder
    );
  }

  public createOrderWithExistingInvoice(order: AddExistingInvoiceDto) {
    return this.http.post<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orders/inbound/existing-invoice`,
      order
    );
  }

  public uploadDocumentsFile(documents) {
    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments`,
      documents
    );
  }

  public editOrder(order: GenerateInvoiceDto) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/update-order-existing-invoice/${order.id}`,
      order
    );
  }

  public orderReview(reviewData: ReviewOrderDto) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/approve-order/${reviewData.id}`,
      reviewData
    );
  }

  public reprovePayment(orderId: number, reproveData: RefusalPaymentDto) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/refusal-payment/${orderId}`,
      reproveData
    );
  }

  public approvePayment(orderId: number, approveData: ApprovePaymentDto) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/identify-payment/${orderId}`,
      approveData
    );
  }

  public massIdentifyPayment(payload: {
    values: { orderId: number; valueApprovedPayment: number }[];
  }) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/identify-payment`,
      payload
    );
  }

  public treasuryInfo(orderId: number, treasuryData: TreasuryInfoDto) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/treasury/${orderId}`,
      treasuryData
    );
  }

  public exchangeInfo(orderId: number, slipCode: string) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/slip/${orderId}`,
      slipCode
    );
  }

  public seetlementConfirm(orderId: number, settlement: FormData) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/settle-order/${orderId}`,
      settlement
    );
  }

  public uploadOrderAttachments(orderId: number, attachments: FormData) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/upload-order-attachments/${orderId}`,
      attachments
    );
  }

  public cancelOrder(canceledData: CancelOrderDto) {
    return this.http.post(
      `${environment.API_BASE_URL}/v1/orders/admin/cancel-order-admin/${canceledData.id}`,
      canceledData
    );
  }

  public cancelQuotation(orderId: number) {
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/cancel-quotation/${orderId}`,
      orderId
    );
  }

  public financialReconciliation(conciliationData: ConciliationDto) {
    return this.http.post(
      `${environment.API_BASE_URL}/v1/orders/admin/identify-payment-outside-inbound`,
      conciliationData
    );
  }

  public createOrderInboundAdmin(orderData: CreateOrderAdminDto) {
    return this.http.post(
      `${environment.API_BASE_URL}/v1/orders/admin/create-order-inbound`,
      orderData
    );
  }

  public sendEmailExistingInvoice(orderId: number) {
    return this.http.post<ResponseObjectModel<any>>(
      `${environment.API_BASE_URL}/v1/orders/inbound/send-email-existing-invoice/${orderId}`,
      {}
    );
  }

  public uploadInvoiceFile(OrderId: string, file: any) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('orderId', OrderId);

    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments/invoice`,
      formData
    );
  }

  public uploadInvoiceFileOutbound(
    orderId: number,
    arrFiles: FileHandle[],
    diNumber: string,
    diProtocol: string
  ) {
    const formData = new FormData();
    for (let file of arrFiles) {
      formData.append('file', file.file);
    }
    formData.append('protocoloDI', diProtocol);
    formData.append('numeroDI', diNumber);
    formData.append('orderId', orderId.toString());

    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments/invoice-outbound`,
      formData
    );
  }

  public uploadInvoiceFileInbound(
    orderId: number,
    updateOrder: UpdateOrderWithInvoiceDto
  ) {
    const formData = new FormData();
    for (let file of updateOrder.files) {
      formData.append('file', file.file);
    }
    formData.append('protocoloDUE', updateOrder.dueProtocol);
    formData.append('numeroDUE', updateOrder.dueNumber);
    formData.append('finalityId', updateOrder.finalityId.toString());
    formData.append('orderId', orderId.toString());

    return this.http.post<ResponseObjectModel<InboundOrderDto>>(
      `${environment.API_BASE_URL}/v1/orderattachments/invoice-inbound`,
      formData
    );
  }

  public seetlementConfirmExchange(orderId: number, documents: any) {
    const formData = new FormData();
    formData.append(
      'ExchangeContract',
      documents.settlement.exchangeContractFile
    );
    formData.append('ProofOperation', documents.settlement.swiftFile);
    formData.append('SendEmail', documents.settlement.sendEmail);
    for (let file of documents.operation) {
      formData.append('OtherFiles', file.file);
    }
    return this.http.put(
      `${environment.API_BASE_URL}/v1/orders/admin/settle-order/${orderId}`,
      formData
    );
  }

  public uploadReceiptFileOutbound(
    orderId: number,
    file: any,
    diNumber: string,
    diProtocol: string
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('protocoloDI', diProtocol);
    formData.append('numeroDI', diNumber);
    formData.append('orderId', orderId.toString());

    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments/receipt-outbound`,
      formData
    );
  }

  public validateUploadInvoiceFileOutbound(
    orderId: number,
    arrFiles: FileHandle[],
    diNumber: string,
    diProtocol: string
  ) {
    const formData = new FormData();
    for (let file of arrFiles) {
      formData.append('file', file.file);
    }
    formData.append('protocoloDI', diProtocol);
    formData.append('numeroDI', diNumber);
    formData.append('orderId', orderId.toString());

    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments/invoice-outbound/validate`,
      formData
    );
  }

  public validateUploadInvoiceFileInbound(
    updateOrder: UpdateOrderWithInvoiceDto
  ) {
    const formData = new FormData();
    for (let file of updateOrder.files) {
      formData.append('file', file.file);
    }
    formData.append('protocoloDUE', updateOrder.dueProtocol);
    formData.append('numeroDUE', updateOrder.dueNumber);
    formData.append('finalityId', updateOrder.finalityId.toString());
    formData.append('orderId', updateOrder.orderId.toString());

    return this.http.post<any>(
      `${environment.API_BASE_URL}/v1/orderattachments/invoice-inbound/validate`,
      formData
    );
  }

  public downloadOrderAttachment(orderAttachmentId: number) {
    return this.http.get(
      `${environment.API_BASE_URL}/v1/orderattachments/${orderAttachmentId}/download`,
      { responseType: 'blob' }
    );
  }

  public downloadOrderAttachmentByName(nameDocument: string) {
    return this.http.get(
      `${environment.API_BASE_URL}/v1/orderattachments/${nameDocument}/download-order-document`,
      { responseType: 'blob' }
    );
  }

  public downloadAttachmentMobile(response: any, nameFile: string) {
    const file = new Blob([response], {
      type: response.type || 'application/octet-stream',
    });
    const blob = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blob;
    link.download = nameFile;
    document.body.appendChild(link);
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );
    setTimeout(() => {
      window.URL.revokeObjectURL(blob);
      link.remove();
    }, 1000);
  }

  public sendConciliation(order: InboundOrderDto) {
    return this.http.put<ResponseObjectModel<any>>(
      `${environment.API_BASE_URL}/v1/orders/admin/quotation-order/${order.id}`,
      order
    );
  }

  downloadDocuments(docName: string) {
    return this.http.get(
      `${environment.API_BASE_URL}/v1/orderattachments/${docName}/download-document`,
      { responseType: 'blob' }
    );
  }

  validateQuotation(orderId: number) {
    return this.http.put<any>(
      `${environment.API_BASE_URL}/v1/orders/validate-quotation/${orderId}`,
      orderId
    );
  }

  public sendDeal(dealDto: CreateDealDto) {
    return this.http.post(
      `${environment.API_BASE_URL}/v1/orders/deal`,
      dealDto
    );
  }

  public reprocess(boletoNumber: any) {
    return this.http.get(
      `${environment.API_BASE_URL}/v1/orders/exchanges/integrate?number=${boletoNumber}`
    );
  }

  public import(boletoNumber: any) {
    return this.http.get(
      `${environment.API_BASE_URL}/v1/orders/exchanges/import?number=${boletoNumber}`
    );
  }

  public getQuotationStatus() {
    return this.http.get<ResponseObjectModel<QuotationStatus>>(
      `${environment.API_BASE_URL}/v1/stocks/health`
    );
  }

  public forceReconnect() {
    return this.http.get<any>(
      `${environment.API_BASE_URL}/v1/stocks/reconnect`
    );
  }

  public forceDisconnect() {
    return this.http.get<any>(
      `${environment.API_BASE_URL}/v1/stocks/disconnect`
    );
  }

  public getConfigs() {
    return this.http.get<ResponseObjectModel<ConfigsDto>>(
      `${environment.API_BASE_URL}/v1/configs/1`
    );
  }

  public toggleQuotation(quotationType: string, toggle: boolean) {
    return this.http.patch<ResponseObjectModel<ConfigsDto>>(
      `${environment.API_BASE_URL}/v1/configs/1/quotation/${quotationType}?enabled=${toggle}`,
      null
    );
  }

  public getQuotationProvidersOverview() {
    return this.http.get<
      ResponseObjectModel<QuotationProvidersOverviewResponse>
    >(
      `${environment.API_BASE_URL}/v1/configs/quotations/providers/overview`
    );
  }

  public switchQuotationProvider(providerId: string) {
    return this.http.put<ResponseObjectModel<any>>(
      `${environment.API_BASE_URL}/v1/configs/quotations/providers/switch?providerId=${providerId}`,
      null
    );
  }

  public getSwiftReceipts(
    pageNumber?: number,
    pageSize?: number,
    search?: string
  ) {
    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}&swift=true`;
    if (search) {
      queryParams += `&filter=${encodeURIComponent(search)}`;
    }
    return this.http.get<
      ResponseObjectModel<ResponseTableModel<any[]>>
    >(`${environment.API_BASE_URL}/v1/orders/staff/swift?${queryParams}`);
  }

  public approveSwiftReceipt(orderId: number) {
    return this.http.patch<ResponseObjectModel<any>>(
      `${environment.API_BASE_URL}/v1/orders/staff/swift/${orderId}?approval=true`,
      null
    );
  }

  public rejectSwiftReceipt(orderId: number) {
    return this.http.patch<ResponseObjectModel<any>>(
      `${environment.API_BASE_URL}/v1/orders/staff/swift/${orderId}?approval=false`,
      null
    );
  }
}
